/**
 * /admin/offres-partenaires — saisie manuelle des prix négociés.
 *
 * Ce catalogue alimente le comparateur, donc le site ET l'app iOS.
 * Il remplace `seed_data.py` (jeu de démonstration aux prix de ~2024).
 *
 * Garde-fou clé : chaque offre est confrontée au tarif réglementé sur un profil
 * de référence (6 kVA / 4 800 kWh). Une offre « négociée » plus chère que le TRV
 * n'apporte rien au client — elle est signalée en rouge et ne doit pas être proposée.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/admin/ConfirmProvider";
import {
    Loader2, ShieldAlert, Plus, Tag, CheckCircle2, AlertTriangle, Leaf, Pencil,
    Trash2, History, RefreshCw, Search, Download, Zap, Flame, TrendingUp, TrendingDown,
    ChevronLeft, ChevronRight, ChevronDown, Home, Lightbulb, Info, ArrowRight, Filter, X,
    LayoutGrid,
} from "lucide-react";

const ALLOWED_KVA = [3, 6, 9, 12, 15, 18, 24, 30, 36];

const EMPTY = {
    supplier_id: "",
    supplier_name: "",
    name: "",
    energy_type: "electricity",
    audience: "b2c",
    offer_type: "indexed",
    option: "base",
    eligible_kva: [6],
    subscription_eur_month: "",
    price_base_eur_kwh: "",
    price_hp_eur_kwh: "",
    price_hc_eur_kwh: "",
    discount_pct: "",
    green: false,
    valid_from: new Date().toISOString().slice(0, 10),
    valid_until: "",
    notes: "",
};

const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`);
const num = (v, d = 3) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d }));

/**
 * Écart au tarif réglementé, en pourcentage du TRV.
 *
 * Attention au dénominateur : l'économie rapportée au *prix de l'offre* donne un
 * pourcentage plus flatteur (181/938 = 19,3 %) que l'économie rapportée au *TRV*
 * (181/1119 = 16,1 %). Seule la seconde répond à la question « combien de moins
 * que le tarif réglementé ? ». Pour un courtier, afficher la première serait une
 * information trompeuse.
 */
const pctVsTrv = (o) => {
    if (o.annual_cost_ref_eur == null || !o.regulated_ref_eur) return null;
    return ((o.annual_cost_ref_eur - o.regulated_ref_eur) / o.regulated_ref_eur) * 100;
};

const OFFER_TYPE_LABEL = { fixed: "Fixe", indexed: "Indexée" };
const OFFER_TYPE_HINT = {
    fixed: "Prix du kWh bloqué pendant toute la durée du contrat.",
    indexed: "Prix indexé sur le tarif réglementé : à revérifier à chaque révision.",
};

/** Initiales du fournisseur, pour la pastille de la première colonne. */
const initials = (name) =>
    (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase().slice(0, 2);

/** Anneau de répartition — deux segments, sans dépendance de graphique. */
function Donut({ a, b, labelA, labelB }) {
    const total = a + b;
    const R = 26, C = 2 * Math.PI * R;
    const lenA = total ? (a / total) * C : 0;
    return (
        <div className="flex items-center gap-4">
            <svg viewBox="0 0 64 64" className="h-16 w-16 shrink-0 -rotate-90" aria-hidden>
                <circle cx="32" cy="32" r={R} fill="none" stroke="hsl(var(--secondary))" strokeWidth="9" />
                {total > 0 && (
                    <circle cx="32" cy="32" r={R} fill="none" stroke="hsl(var(--primary))" strokeWidth="9"
                        strokeDasharray={`${lenA} ${C - lenA}`} />
                )}
            </svg>
            <div className="text-xs space-y-1.5">
                <p className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-primary" />{labelA}
                    <span className="text-muted-foreground">{total ? Math.round((a / total) * 100) : 0}% ({a})</span>
                </p>
                <p className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-secondary ring-1 ring-border" />{labelB}
                    <span className="text-muted-foreground">{total ? Math.round((b / total) * 100) : 0}% ({b})</span>
                </p>
            </div>
        </div>
    );
}

/** Carte KPI : valeur, pastille d'icône, badge secondaire, lien de filtrage. */
function Kpi({ icon: Icon, tint, label, value, badge, badgeTone = "neutral", onDetail, detailLabel = "Voir le détail", danger }) {
    const tones = {
        neutral: "bg-secondary text-muted-foreground",
        good: "bg-emerald-50 text-emerald-700",
        bad: "bg-destructive/10 text-destructive",
    };
    return (
        <Card className={`rounded-2xl ${danger ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-xs em-eyebrow leading-tight">{label}</p>
                    {Icon && (
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                            <Icon className="h-4 w-4" />
                        </span>
                    )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <p className={`font-display text-2xl font-black ${danger ? "text-destructive" : "text-primary"}`}>{value}</p>
                    {badge != null && (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${tones[badgeTone]}`}>{badge}</span>
                    )}
                </div>
                {onDetail && (
                    <button type="button" onClick={onDetail}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                        {detailLabel} <ArrowRight className="h-3 w-3" />
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

export default function AdminPartnerOffers() {
    const { user, loading: authLoading } = useAuth();
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY);
    // Liste des fournisseurs existants (module « Fournisseurs ») pour pré-remplir
    // le champ. `newSupplier` bascule sur la saisie manuelle d'un nouveau.
    const [suppliers, setSuppliers] = useState([]);
    const [newSupplier, setNewSupplier] = useState(false);
    // null = création ; sinon id de l'offre en cours de modification.
    const [editingId, setEditingId] = useState(null);

    // Recherche, filtres et pagination — tout côté client : le catalogue est
    // saisi à la main et restera de l'ordre de la dizaine d'offres.
    const [q, setQ] = useState("");
    const [fEnergy, setFEnergy] = useState("all");
    const [fType, setFType] = useState("all");
    const [fStatus, setFStatus] = useState("active");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [fAudience, setFAudience] = useState("all");
    const [fGreen, setFGreen] = useState("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [dense, setDense] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((o) => {
            if (fStatus === "active" && !o.active) return false;
            if (fStatus === "archived" && o.active) return false;
            if (fEnergy !== "all" && o.energy_type !== fEnergy) return false;
            if (fType !== "all" && o.offer_type !== fType) return false;
            if (fAudience !== "all" && o.audience !== fAudience) return false;
            if (fGreen === "yes" && !o.green) return false;
            if (fGreen === "no" && o.green) return false;
            if (!needle) return true;
            return [o.supplier_name, o.name, o.energy_type, OFFER_TYPE_LABEL[o.offer_type], o.audience]
                .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
        });
    }, [items, q, fEnergy, fType, fStatus, fAudience, fGreen]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
    // Un filtre qui réduit la liste peut laisser la page courante hors bornes.
    const safePage = Math.min(page, pageCount);
    const paged = filtered.slice((safePage - 1) * perPage, safePage * perPage);
    const activeFilters = [fEnergy, fType, fAudience, fGreen].filter((v) => v !== "all").length
        + (fStatus !== "active" ? 1 : 0) + (q.trim() ? 1 : 0);

    const resetFilters = () => {
        setQ(""); setFEnergy("all"); setFType("all"); setFStatus("active");
        setFAudience("all"); setFGreen("all"); setPage(1);
    };

    /** Déclenche le téléchargement d'un contenu généré côté navigateur. */
    const download = (content, mime, filename) => {
        const blob = new Blob([content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const stamp = () => new Date().toISOString().slice(0, 10);

    /** Export brut, pour retraitement. Contient les mêmes lignes que l'écran. */
    const exportJson = () => {
        if (!filtered.length) { toast.error("Aucune offre à exporter."); return; }
        download(JSON.stringify(filtered, null, 2), "application/json;charset=utf-8;",
            `offres-partenaires-${stamp()}.json`);
        toast.success(`${filtered.length} offre(s) exportée(s) en JSON.`);
    };

    /** Export CSV des lignes actuellement filtrées (séparateur ';' pour Excel FR). */
    const exportCsv = () => {
        if (!filtered.length) { toast.error("Aucune offre à exporter."); return; }
        const cols = [
            ["Fournisseur", (o) => o.supplier_name],
            ["Offre", (o) => o.name],
            ["Énergie", (o) => (o.energy_type === "gas" ? "Gaz" : "Électricité")],
            ["Type", (o) => OFFER_TYPE_LABEL[o.offer_type] || o.offer_type],
            ["Option", (o) => o.option || ""],
            ["Clientèle", (o) => o.audience],
            ["Prix kWh (€)", (o) => o.option === "hc" ? `${o.price_hp_eur_kwh} / ${o.price_hc_eur_kwh}` : o.price_base_eur_kwh],
            ["Remise (%)", (o) => o.discount_pct ?? ""],
            ["Abonnement (€/mois)", (o) => o.subscription_eur_month],
            ["Coût réf. (€/an)", (o) => o.annual_cost_ref_eur ?? ""],
            ["TRV réf. (€/an)", (o) => o.regulated_ref_eur ?? ""],
            ["Écart au TRV (%)", (o) => { const p = pctVsTrv(o); return p == null ? "" : p.toFixed(1); }],
            ["Verte", (o) => (o.green ? "oui" : "non")],
            ["Statut", (o) => (o.active ? "active" : "archivée")],
        ];
        // Un champ contenant ';' ou '"' doit être entouré de guillemets doublés.
        const esc = (v) => {
            const s = String(v ?? "");
            return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = [cols.map((c) => c[0]), ...filtered.map((o) => cols.map((c) => c[1](o)))];
        const csv = rows.map((r) => r.map(esc).join(";")).join("\r\n");
        // BOM UTF-8 : sans lui, Excel affiche « Ã© » à la place des accents.
        download("﻿" + csv, "text/csv;charset=utf-8;", `offres-partenaires-${stamp()}.csv`);
        toast.success(`${filtered.length} offre(s) exportée(s) en CSV.`);
    };

    /** Ouvre le formulaire vierge (création). */
    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY);
        setNewSupplier(false);
        setShowForm(true);
    };

    /** Ouvre le formulaire pré-rempli avec une offre existante (modification). */
    const openEdit = (o) => {
        const s = (v) => (v == null ? "" : String(v));
        setForm({
            supplier_id: o.supplier_id || "",
            supplier_name: o.supplier_name || "",
            name: o.name || "",
            energy_type: o.energy_type || "electricity",
            audience: o.audience || "b2c",
            offer_type: o.offer_type || "indexed",
            option: o.option || "base",
            eligible_kva: o.eligible_kva?.length ? o.eligible_kva : [6],
            subscription_eur_month: s(o.subscription_eur_month),
            price_base_eur_kwh: s(o.price_base_eur_kwh),
            price_hp_eur_kwh: s(o.price_hp_eur_kwh),
            price_hc_eur_kwh: s(o.price_hc_eur_kwh),
            discount_pct: s(o.discount_pct),
            green: !!o.green,
            valid_from: o.valid_from || new Date().toISOString().slice(0, 10),
            valid_until: o.valid_until || "",
            notes: o.notes || "",
        });
        // Si le fournisseur n'existe plus dans le module Fournisseurs, on repasse
        // en saisie libre pour ne pas perdre la valeur enregistrée.
        const known = suppliers.some((x) => (x.id || x.slug) === o.supplier_id);
        setNewSupplier(!known);
        setEditingId(o.id);
        setShowForm(true);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [o, s, sup] = await Promise.all([
                api.get("/admin/partner-offers"),
                api.get("/admin/partner-offers/summary"),
                api.get("/suppliers"),
            ]);
            setItems(o.data);
            setSummary(s.data);
            setSuppliers(Array.isArray(sup.data) ? sup.data : []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    /** Convertit les champs texte en nombres ; omet ceux qui ne concernent pas l'option. */
    const buildPayload = () => {
        const isGas = form.energy_type === "gas";
        const num = (v) => (v === "" || v == null ? null : Number(v));
        const p = {
            supplier_id: form.supplier_id.trim(),
            supplier_name: form.supplier_name.trim(),
            name: form.name.trim(),
            energy_type: form.energy_type,
            audience: form.audience,
            offer_type: form.offer_type,
            option: isGas ? null : form.option,
            eligible_kva: form.eligible_kva,
            subscription_eur_month: num(form.subscription_eur_month),
            discount_pct: num(form.discount_pct),
            green: form.green,
            valid_from: form.valid_from,
            valid_until: form.valid_until || null,
            notes: form.notes || null,
        };
        // Le backend refuse les prix incohérents avec l'option : on n'envoie que les bons.
        if (isGas || form.option === "base") {
            p.price_base_eur_kwh = num(form.price_base_eur_kwh);
        } else {
            p.price_hp_eur_kwh = num(form.price_hp_eur_kwh);
            p.price_hc_eur_kwh = num(form.price_hc_eur_kwh);
        }
        return p;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.supplier_id.trim() || !form.supplier_name.trim()) {
            toast.error("Choisis un fournisseur, ou renseigne l'identifiant et le nom du nouveau.");
            return;
        }
        setSaving(true);
        try {
            const res = editingId
                ? await api.patch(`/admin/partner-offers/${editingId}`, buildPayload())
                : await api.post("/admin/partner-offers", buildPayload());
            const d = res.data;
            if (d.beats_regulated === false) {
                toast.warning("Offre enregistrée, mais elle est PLUS CHÈRE que le tarif réglementé.");
            } else {
                toast.success(editingId ? "Offre mise à jour." : "Prix négocié enregistré.");
            }
            setShowForm(false);
            setForm(EMPTY);
            setNewSupplier(false);
            setEditingId(null);
            await loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    /**
     * L'icône est une corbeille, mais l'action archive : l'offre est désactivée,
     * jamais supprimée. Elle a pu servir de base à une recommandation faite à un
     * client, et cette trace doit rester auditable. Le dialogue le dit explicitement,
     * pour que l'icône n'induise personne en erreur.
     */
    const archive = async (o) => {
        const okToArchive = await confirm({
            title: "Archiver cette offre ?",
            message: `« ${o.name} » (${o.supplier_name}) sera retirée du comparateur. Elle n'est pas supprimée : elle reste consultable via le filtre « Archivées » et dans le journal des modifications.`,
            confirmLabel: "Archiver",
            danger: true,
        });
        if (!okToArchive) return;
        try {
            await api.post(`/admin/partner-offers/${o.id}/archive`);
            toast.success("Offre archivée (conservée pour l'audit).");
            await loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (authLoading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" />
            </div>
        );
    }
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90">
                    <Link to="/">Retour à l'accueil</Link>
                </Button>
            </div>
        );
    }

    const isGas = form.energy_type === "gas";

    const beatsPct = summary?.electricity_active
        ? Math.round((summary.beats_regulated / summary.electricity_active) * 100) : 0;
    const worsePct = summary?.electricity_active
        ? Math.round((summary.not_beating_regulated / summary.electricity_active) * 100) : 0;

    return (
        <div className="em-container py-8" data-testid="admin-partner-offers-page">
            {/* Fil d'Ariane + actions de tête */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Link to="/admin" className="hover:text-primary"><Home className="h-4 w-4" /></Link>
                    <span aria-hidden>/</span>
                    <span>Administration</span>
                    <span aria-hidden>/</span>
                    <span className="font-semibold text-primary">Offres partenaires</span>
                </nav>
                <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="outline" className="rounded-full" data-testid="audit-link">
                        <Link to="/admin/audit"><History className="h-4 w-4 mr-2" />Journal des modifications</Link>
                    </Button>
                    <Button type="button" onClick={openCreate}
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold" data-testid="new-offer-btn">
                        <Plus className="h-4 w-4 mr-2" />Nouveau prix négocié
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 mt-6 items-start">
                {/* ─────────── Colonne principale ─────────── */}
                <div>
                    <div className="max-w-3xl">
                        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary">
                            Offres partenaires — prix négociés
                        </h1>
                        <p className="text-muted-foreground mt-3">
                            Ce catalogue alimente le comparateur du site et de l'application. Chaque offre est
                            confrontée au tarif réglementé sur un profil de référence&nbsp;: une offre plus chère
                            que le Tarif Bleu n'apporte aucune économie au client.
                        </p>
                    </div>

                    {/* Bandeau d'explication — lève l'ambiguïté du « n/a » sur le gaz */}
                    {summary && (
                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl bg-secondary px-4 py-3 text-sm">
                            <span className="flex items-center gap-2">
                                <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <strong className="font-semibold">Profil de référence :</strong>
                                {summary.reference_profile.puissance_kva} kVA, {summary.reference_profile.annual_kwh.toLocaleString("fr-FR")} kWh/an
                            </span>
                            {summary.reference_profile.trv_annual_eur != null && (
                                <span>
                                    <strong className="font-semibold">Tarif réglementé (repère) :</strong>{" "}
                                    {eur(summary.reference_profile.trv_annual_eur)}/an
                                </span>
                            )}
                            <span className="text-muted-foreground">
                                <strong className="font-semibold text-foreground">Comparaison au TRV :</strong> électricité
                                {" "}(le tarif réglementé du gaz a disparu en 2023, les offres gaz s'affichent donc en «&nbsp;n/a&nbsp;»).
                            </span>
                        </div>
                    )}

                    {/* KPI — santé du catalogue */}
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="offers-summary">
                            <Kpi
                                icon={Tag} tint="bg-secondary text-primary" label="Offres actives" value={summary.active}
                                badge={summary.created_last_30d > 0 ? `+${summary.created_last_30d} ce mois-ci` : null}
                                onDetail={() => { resetFilters(); }} detailLabel="Voir toutes les offres"
                            />
                            <Kpi
                                icon={Zap} tint="bg-sky-50 text-sky-600" label="Électricité active" value={summary.electricity_active}
                                onDetail={() => { setFEnergy("electricity"); setFStatus("active"); setPage(1); }}
                            />
                            <Kpi
                                icon={Flame} tint="bg-orange-50 text-orange-600" label="Gaz actif" value={summary.gas_active ?? 0}
                                onDetail={() => { setFEnergy("gas"); setFStatus("active"); setPage(1); }}
                            />
                            <Kpi
                                icon={CheckCircle2} tint="bg-emerald-50 text-emerald-600" label="Battent le TRV"
                                value={summary.beats_regulated} badge={`${beatsPct}%`} badgeTone="good"
                                onDetail={() => { setFEnergy("electricity"); setFStatus("active"); setPage(1); }}
                                detailLabel="Voir les offres"
                            />
                        </div>
                    )}

                    {/* Recherche + filtres */}
                    <Card className="rounded-3xl border-border mt-6">
                        <CardContent className="p-4">
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="relative min-w-[220px] flex-1">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                                        placeholder="Rechercher par fournisseur, offre, type…" className="pl-9 rounded-xl"
                                        data-testid="offer-search" />
                                </div>
                                <div className="min-w-[150px]">
                                    <p className="text-xs em-eyebrow mb-1.5">Type d'énergie</p>
                                    <Select value={fEnergy} onValueChange={(v) => { setFEnergy(v); setPage(1); }}>
                                        <SelectTrigger className="rounded-xl" data-testid="filter-energy"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tous</SelectItem>
                                            <SelectItem value="electricity">Électricité</SelectItem>
                                            <SelectItem value="gas">Gaz</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="min-w-[150px]">
                                    <p className="text-xs em-eyebrow mb-1.5">Type d'offre</p>
                                    <Select value={fType} onValueChange={(v) => { setFType(v); setPage(1); }}>
                                        <SelectTrigger className="rounded-xl" data-testid="filter-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tous</SelectItem>
                                            <SelectItem value="fixed">Fixe</SelectItem>
                                            <SelectItem value="indexed">Indexée</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="min-w-[150px]">
                                    <p className="text-xs em-eyebrow mb-1.5">Statut</p>
                                    <Select value={fStatus} onValueChange={(v) => { setFStatus(v); setPage(1); }}>
                                        <SelectTrigger className="rounded-xl" data-testid="filter-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Actives</SelectItem>
                                            <SelectItem value="archived">Archivées</SelectItem>
                                            <SelectItem value="all">Toutes</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button type="button" variant="outline" className="rounded-xl"
                                    onClick={() => setShowAdvanced((v) => !v)} data-testid="advanced-filters">
                                    <Filter className="h-4 w-4 mr-2" />Filtres avancés
                                </Button>
                            </div>

                            {showAdvanced && (
                                <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-4">
                                    <div className="min-w-[170px]">
                                        <p className="text-xs em-eyebrow mb-1.5">Clientèle</p>
                                        <Select value={fAudience} onValueChange={(v) => { setFAudience(v); setPage(1); }}>
                                            <SelectTrigger className="rounded-xl" data-testid="filter-audience"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Toutes</SelectItem>
                                                <SelectItem value="b2c">Particuliers</SelectItem>
                                                <SelectItem value="b2b">Professionnels</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="min-w-[170px]">
                                        <p className="text-xs em-eyebrow mb-1.5">Offre verte</p>
                                        <Select value={fGreen} onValueChange={(v) => { setFGreen(v); setPage(1); }}>
                                            <SelectTrigger className="rounded-xl" data-testid="filter-green"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Indifférent</SelectItem>
                                                <SelectItem value="yes">Vertes uniquement</SelectItem>
                                                <SelectItem value="no">Non vertes</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}

                            {activeFilters > 0 && (
                                <button type="button" onClick={resetFilters}
                                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary"
                                    data-testid="reset-filters">
                                    <X className="h-3.5 w-3.5" />
                                    Réinitialiser les filtres ({activeFilters})
                                </button>
                            )}
                        </CardContent>
                    </Card>

                    {/* Tableau */}
                    <Card className="rounded-3xl border-border mt-6">
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="py-20 text-center text-muted-foreground" data-testid="offers-loading">
                                    <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                                </div>
                            ) : items.length === 0 ? (
                                <div className="py-16 text-center text-muted-foreground" data-testid="offers-empty">
                                    <Tag className="h-12 w-12 mx-auto text-primary/30" />
                                    <p className="mt-3 font-medium text-primary">Aucun prix négocié</p>
                                    <p className="text-sm mt-1">
                                        Tant que ce catalogue est vide, le comparateur sert des données de démonstration.
                                    </p>
                                </div>
                            ) : filtered.length === 0 ? (
                                <div className="py-16 text-center text-muted-foreground" data-testid="offers-no-match">
                                    <Search className="h-10 w-10 mx-auto text-primary/30" />
                                    <p className="mt-3 font-medium text-primary">Aucune offre ne correspond</p>
                                    <button type="button" onClick={resetFilters} className="text-sm mt-1 font-semibold text-primary hover:underline">
                                        Réinitialiser les filtres
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
                                        <p className="text-sm text-muted-foreground" data-testid="offers-count">
                                            {filtered.length} offre{filtered.length > 1 ? "s" : ""} trouvée{filtered.length > 1 ? "s" : ""}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button type="button" variant="outline" className="rounded-xl" data-testid="export-menu">
                                                        <Download className="h-4 w-4 mr-2" />Exporter
                                                        <ChevronDown className="h-4 w-4 ml-2 opacity-60" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuLabel>{filtered.length} ligne(s) filtrée(s)</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={exportCsv} data-testid="export-csv">
                                                        CSV (Excel, séparateur «&nbsp;;&nbsp;»)
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={exportJson} data-testid="export-json">
                                                        JSON (données brutes)
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button type="button" variant="outline" className="rounded-xl" aria-label="Densité d'affichage" data-testid="density-menu">
                                                        <LayoutGrid className="h-4 w-4" />
                                                        <ChevronDown className="h-4 w-4 ml-2 opacity-60" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuLabel>Densité d'affichage</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => setDense(false)}>
                                                        {!dense && "✓ "}Confortable
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => setDense(true)}>
                                                        {dense && "✓ "}Compacte
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[1000px]" data-testid="partner-offers-table">
                                            <thead className="bg-secondary">
                                                <tr className="text-left align-bottom">
                                                    <th className="px-5 py-4 em-eyebrow">Fournisseur</th>
                                                    <th className="px-5 py-4 em-eyebrow">Offre</th>
                                                    <th className="px-5 py-4 em-eyebrow">Type d'énergie</th>
                                                    <th className="px-5 py-4 em-eyebrow">Type d'offre</th>
                                                    <th className="px-5 py-4 em-eyebrow">Prix kWh<br /><span className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground">(€/kWh TTC)</span></th>
                                                    <th className="px-5 py-4 em-eyebrow">Abo/mois<br /><span className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground">(€/mois TTC)</span></th>
                                                    <th className="px-5 py-4 em-eyebrow">Coût annuel réf.<br /><span className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground">(€ TTC)</span></th>
                                                    <th className="px-5 py-4 em-eyebrow">Économie annuelle<br />vs TRV <span className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground">(€ TTC)</span></th>
                                                    <th className="px-5 py-4 em-eyebrow">Statut</th>
                                                    <th className="px-5 py-4 em-eyebrow text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paged.map((o) => {
                                                    const cellY = dense ? "py-2.5" : "py-4";
                                                    const saving = o.regulated_ref_eur != null && o.annual_cost_ref_eur != null
                                                        ? o.regulated_ref_eur - o.annual_cost_ref_eur : null;
                                                    return (
                                                        <tr key={o.id} className={`border-t border-border ${o.active ? "" : "opacity-50"}`}
                                                            data-testid={`offer-row-${o.id}`}>
                                                            <td className={`px-5 ${cellY}`}>
                                                                <div className="flex items-center gap-3">
                                                                    <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-bold text-primary">
                                                                        {initials(o.supplier_name)}
                                                                    </span>
                                                                    <div>
                                                                        <p className="font-display font-bold flex items-center gap-1.5 leading-tight">
                                                                            {o.supplier_name}
                                                                            {o.green && <Leaf className="h-3.5 w-3.5 text-emerald-600" aria-label="Offre verte" />}
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">{o.audience}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className={`px-5 ${cellY} text-sm font-medium`}>{o.name}</td>
                                                            <td className={`px-5 ${cellY} text-sm`}>
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    {o.energy_type === "gas"
                                                                        ? <><Flame className="h-4 w-4 text-orange-500" aria-hidden />Gaz</>
                                                                        : <><Zap className="h-4 w-4 text-sky-500" aria-hidden />Électricité</>}
                                                                </span>
                                                            </td>
                                                            <td className={`px-5 ${cellY} text-sm`}>
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    {OFFER_TYPE_LABEL[o.offer_type] || o.offer_type}
                                                                    {o.option && <span className="text-muted-foreground">· {o.option}</span>}
                                                                    <Info className="h-3.5 w-3.5 text-muted-foreground/70" aria-label={OFFER_TYPE_HINT[o.offer_type]}>
                                                                        <title>{OFFER_TYPE_HINT[o.offer_type]}</title>
                                                                    </Info>
                                                                </span>
                                                            </td>
                                                            <td className={`px-5 ${cellY} text-sm font-mono`}>
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    {o.option === "hc"
                                                                        ? `${num(o.price_hp_eur_kwh)} / ${num(o.price_hc_eur_kwh)}`
                                                                        : num(o.price_base_eur_kwh)}
                                                                    {o.offer_type === "indexed"
                                                                        ? <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-label="Prix indexé, susceptible d'évoluer" />
                                                                        : <TrendingDown className="h-3.5 w-3.5 text-emerald-600" aria-label="Prix bloqué" />}
                                                                </span>
                                                                {o.discount_pct ? <span className="ml-1 text-xs text-emerald-700">−{o.discount_pct}%</span> : null}
                                                            </td>
                                                            <td className={`px-5 ${cellY} text-sm font-mono`}>{eur(o.subscription_eur_month)}</td>
                                                            <td className={`px-5 ${cellY} text-sm font-mono`}>{eur(o.annual_cost_ref_eur)}</td>
                                                            {/* Économie exprimée en positif : « 181 € / an » se lit mieux que
                                                                « −181 € ». Aucun pourcentage ici — le seul dénominateur honnête
                                                                serait le TRV, et le chiffre en euros suffit à décider. Le mot
                                                                « estimée » n'est pas cosmétique : le calcul porte sur le profil
                                                                de référence, pas sur la consommation réelle du client. */}
                                                            <td className={`px-5 ${cellY}`}>
                                                                {o.beats_regulated == null ? (
                                                                    <div className="text-xs text-muted-foreground">
                                                                        <p>n/a</p>
                                                                        <p className="text-[11px]">pas de TRV gaz</p>
                                                                    </div>
                                                                ) : o.beats_regulated ? (
                                                                    <div data-testid={`beats-trv-${o.id}`}>
                                                                        <p className="text-[11px] font-semibold text-emerald-700">Économie estimée</p>
                                                                        <p className="text-sm font-bold text-emerald-700">
                                                                            {Math.round(saving).toLocaleString("fr-FR")} € <span className="font-medium">/ an</span>
                                                                        </p>
                                                                        <span className="mt-1 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                                                                            vs TRV
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div data-testid={`worse-trv-${o.id}`}>
                                                                        <p className="text-[11px] font-semibold text-destructive">Surcoût estimé</p>
                                                                        <p className="text-sm font-bold text-destructive">
                                                                            +{Math.round(-saving).toLocaleString("fr-FR")} € <span className="font-medium">/ an</span>
                                                                        </p>
                                                                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-bold text-destructive">
                                                                            <AlertTriangle className="h-3 w-3" />Plus chère
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className={`px-5 ${cellY}`}>
                                                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                                    o.active ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"
                                                                }`}>
                                                                    <span className={`h-1.5 w-1.5 rounded-full ${o.active ? "bg-emerald-600" : "bg-muted-foreground"}`} />
                                                                    {o.active ? "Active" : "Archivée"}
                                                                </span>
                                                            </td>
                                                            <td className={`px-5 ${cellY}`}>
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    <Button variant="outline" size="icon" type="button" className="h-8 w-8 rounded-lg"
                                                                        onClick={() => openEdit(o)} aria-label="Modifier" data-testid={`edit-offer-${o.id}`}>
                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    {o.active && (
                                                                        /* Corbeille par convention visuelle — l'action archive, elle ne supprime pas. */
                                                                        <Button variant="outline" size="icon" type="button"
                                                                            className="h-8 w-8 rounded-lg border-destructive/30 text-destructive hover:bg-destructive/10"
                                                                            onClick={() => archive(o)} aria-label="Archiver cette offre"
                                                                            data-testid={`archive-offer-${o.id}`}>
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-t border-border">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <span>Lignes par page</span>
                                            <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
                                                <SelectTrigger className="h-8 w-[72px] rounded-lg" data-testid="per-page"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Button variant="outline" size="icon" type="button" className="h-8 w-8 rounded-lg"
                                                disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} aria-label="Page précédente">
                                                <ChevronLeft className="h-4 w-4" />
                                            </Button>
                                            <span className="rounded-lg bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground" data-testid="current-page">
                                                {safePage}
                                            </span>
                                            <span className="text-sm text-muted-foreground">/ {pageCount}</span>
                                            <Button variant="outline" size="icon" type="button" className="h-8 w-8 rounded-lg"
                                                disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)} aria-label="Page suivante">
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* ─────────── Colonne latérale : aperçu rapide ─────────── */}
                <aside className="space-y-4 lg:sticky lg:top-6" data-testid="quick-overview">
                    <Card className="rounded-3xl border-border">
                        <CardContent className="p-5 flex items-center justify-between gap-3">
                            <div>
                                <p className="em-eyebrow">Dernière mise à jour</p>
                                <p className="text-sm font-medium mt-1">
                                    {summary?.last_updated
                                        ? new Date(summary.last_updated).toLocaleString("fr-FR",
                                            { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                                        : "—"}
                                </p>
                            </div>
                            <Button type="button" variant="outline" size="icon" className="rounded-xl shrink-0"
                                onClick={loadData} disabled={loading} aria-label="Rafraîchir" data-testid="refresh-btn">
                                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Le repère indépendant, affiché tel quel : c'est la valeur à laquelle
                        toutes les offres sont comparées. La montrer évite que l'admin ait à
                        la deviner à partir des écarts. */}
                    {summary?.reference_profile?.trv_annual_eur != null && (
                        <Card className="rounded-3xl border-border">
                            <CardContent className="p-5">
                                <p className="em-eyebrow">Tarif réglementé de référence</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Tarif Bleu · {summary.reference_profile.puissance_kva} kVA, {summary.reference_profile.annual_kwh.toLocaleString("fr-FR")} kWh/an
                                </p>
                                <p className="mt-2 font-display text-2xl font-black text-primary">
                                    {eur(summary.reference_profile.trv_annual_eur)}<span className="ml-1 text-sm font-semibold text-muted-foreground">/ an</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">
                                    {num(summary.reference_profile.trv_price_kwh, 4)} €/kWh · abonnement {eur(summary.reference_profile.trv_subscription_month)}/mois
                                </p>
                                {summary.reference_profile.trv_valid_from && (
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Grille en vigueur au {new Date(summary.reference_profile.trv_valid_from).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <Card className="rounded-3xl border-border">
                        <CardContent className="p-5">
                            <p className="em-eyebrow">Aperçu rapide</p>

                            {/* Moyenne, pas maximum : le meilleur élément du catalogue promettrait une
                                économie qu'une seule offre peut tenir. La moyenne des offres qui battent
                                réellement le TRV décrit ce que le catalogue vaut. */}
                            <div className="mt-5">
                                <p className="text-sm font-semibold">Économie moyenne estimée</p>
                                <p className="text-xs text-muted-foreground">vs TRV (électricité)</p>
                                <p className="mt-2 font-display text-3xl font-black text-emerald-700">
                                    {summary?.avg_saving_eur != null ? `${summary.avg_saving_eur.toLocaleString("fr-FR")} €` : "—"}
                                    <span className="ml-1 text-sm font-semibold text-muted-foreground">/ an</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {summary?.competitive_count
                                        ? `sur ${summary.competitive_count} offre${summary.competitive_count > 1 ? "s" : ""} compétitive${summary.competitive_count > 1 ? "s" : ""}`
                                        : "aucune offre compétitive"}
                                </p>
                            </div>

                            <div className="mt-6 border-t border-border pt-5">
                                <p className="text-sm font-semibold mb-3">Répartition par type d'offre</p>
                                <Donut
                                    a={summary?.by_offer_type?.fixed ?? 0}
                                    b={summary?.by_offer_type?.indexed ?? 0}
                                    labelA="Fixe" labelB="Indexée"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Placée à côté de l'Astuce qui la commente : le compteur et sa règle
                        (« doit rester à zéro ») se lisent ensemble. */}
                    {summary && (
                        <Card className={`rounded-3xl ${summary.not_beating_regulated > 0 ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-xs em-eyebrow leading-tight">Plus chères que le TRV</p>
                                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                                        summary.not_beating_regulated > 0 ? "bg-destructive/10 text-destructive" : "bg-emerald-50 text-emerald-600"
                                    }`}>
                                        {summary.not_beating_regulated > 0
                                            ? <AlertTriangle className="h-4 w-4" />
                                            : <TrendingUp className="h-4 w-4" />}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <p className={`font-display text-3xl font-black ${summary.not_beating_regulated > 0 ? "text-destructive" : "text-primary"}`}>
                                        {summary.not_beating_regulated}
                                    </p>
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                        summary.not_beating_regulated > 0 ? "bg-destructive/10 text-destructive" : "bg-secondary text-muted-foreground"
                                    }`}>{worsePct}%</span>
                                </div>
                                <button type="button"
                                    onClick={() => { setFEnergy("electricity"); setFStatus("active"); setPage(1); }}
                                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                                    Voir les offres <ArrowRight className="h-3 w-3" />
                                </button>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="rounded-3xl border-border bg-secondary/50">
                        <CardContent className="p-5">
                            <p className="flex items-center gap-2 text-sm font-semibold">
                                <Lightbulb className="h-4 w-4 text-primary" />Astuce
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                                Une offre plus chère que le TRV n'apporte aucune économie au client. Le compteur
                                « Plus chères que le TRV » doit rester à zéro.
                            </p>
                        </CardContent>
                    </Card>
                </aside>
            </div>

            {/* Formulaire de saisie */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {editingId ? "Modifier l'offre négociée" : "Nouveau prix négocié"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="offer-form">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field
                                label="Fournisseur *"
                                hint={newSupplier ? "Renseigne l'identifiant et le nom ci-dessous" : "Issu du module « Fournisseurs »"}
                            >
                                <Select
                                    value={newSupplier ? "__new__" : (form.supplier_id || undefined)}
                                    onValueChange={(v) => {
                                        if (v === "__new__") {
                                            setNewSupplier(true);
                                            setForm((f) => ({ ...f, supplier_id: "", supplier_name: "" }));
                                            return;
                                        }
                                        const s = suppliers.find((x) => (x.id || x.slug) === v);
                                        setNewSupplier(false);
                                        setForm((f) => ({ ...f, supplier_id: v, supplier_name: s?.name || "" }));
                                    }}
                                >
                                    <SelectTrigger data-testid="f-supplier-select">
                                        <SelectValue placeholder="Choisir un fournisseur…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {suppliers.map((s) => (
                                            <SelectItem key={s.id || s.slug} value={s.id || s.slug}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                        <SelectItem value="__new__">+ Nouveau fournisseur…</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            {newSupplier ? (
                                <>
                                    <Field label="Identifiant du nouveau fournisseur *" hint="minuscules, sans espace — ex. primeo, ohm-energie">
                                        <Input
                                            required
                                            value={form.supplier_id}
                                            onChange={(e) => set("supplier_id", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                                            data-testid="f-supplier-id"
                                        />
                                    </Field>
                                    <Field label="Nom du nouveau fournisseur *">
                                        <Input required value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} data-testid="f-supplier-name" />
                                    </Field>
                                </>
                            ) : (
                                <Field label="Nom du fournisseur" hint="Rempli automatiquement">
                                    <Input value={form.supplier_name} readOnly disabled data-testid="f-supplier-name" />
                                </Field>
                            )}
                            <Field label="Nom de l'offre *">
                                <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="f-name" />
                            </Field>
                            <Field label="Énergie">
                                <Select value={form.energy_type} onValueChange={(v) => set("energy_type", v)}>
                                    <SelectTrigger data-testid="f-energy"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="electricity">Électricité</SelectItem>
                                        <SelectItem value="gas">Gaz</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Clientèle">
                                <Select value={form.audience} onValueChange={(v) => set("audience", v)}>
                                    <SelectTrigger data-testid="f-audience"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="b2c">Particuliers</SelectItem>
                                        <SelectItem value="b2b">Professionnels</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Type de prix" hint="Indexée = suit le TRV, à revérifier à chaque révision">
                                <Select value={form.offer_type} onValueChange={(v) => set("offer_type", v)}>
                                    <SelectTrigger data-testid="f-offer-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="fixed">Fixe</SelectItem>
                                        <SelectItem value="indexed">Indexée</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>

                            {!isGas && (
                                <Field label="Option tarifaire">
                                    <Select value={form.option} onValueChange={(v) => set("option", v)}>
                                        <SelectTrigger data-testid="f-option"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="base">Base</SelectItem>
                                            <SelectItem value="hc">Heures creuses</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                            )}

                            <Field label="Abonnement € TTC / mois *">
                                <Input required type="number" step="0.01" min="0" value={form.subscription_eur_month}
                                    onChange={(e) => set("subscription_eur_month", e.target.value)} data-testid="f-subscription" />
                            </Field>

                            {isGas || form.option === "base" ? (
                                <Field label="Prix du kWh € TTC *" hint="ex. 0.1625">
                                    <Input required type="number" step="0.0001" min="0" value={form.price_base_eur_kwh}
                                        onChange={(e) => set("price_base_eur_kwh", e.target.value)} data-testid="f-price-base" />
                                </Field>
                            ) : (
                                <>
                                    <Field label="Prix heures pleines € / kWh *">
                                        <Input required type="number" step="0.0001" min="0" value={form.price_hp_eur_kwh}
                                            onChange={(e) => set("price_hp_eur_kwh", e.target.value)} data-testid="f-price-hp" />
                                    </Field>
                                    <Field label="Prix heures creuses € / kWh *" hint="doit être inférieur aux heures pleines">
                                        <Input required type="number" step="0.0001" min="0" value={form.price_hc_eur_kwh}
                                            onChange={(e) => set("price_hc_eur_kwh", e.target.value)} data-testid="f-price-hc" />
                                    </Field>
                                </>
                            )}

                            <Field label="Remise négociée (%)" hint="appliquée au prix du kWh">
                                <Input type="number" step="0.1" min="0" max="100" value={form.discount_pct}
                                    onChange={(e) => set("discount_pct", e.target.value)} data-testid="f-discount" />
                            </Field>
                            <Field label="Valable à partir du *">
                                <Input required type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid="f-valid-from" />
                            </Field>
                            <Field label="Valable jusqu'au" hint="vide = sans échéance">
                                <Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} data-testid="f-valid-until" />
                            </Field>
                        </div>

                        {!isGas && (
                            <div>
                                <p className="text-sm font-semibold mb-2">Puissances éligibles (kVA) *</p>
                                <div className="flex flex-wrap gap-2">
                                    {ALLOWED_KVA.map((k) => {
                                        const on = form.eligible_kva.includes(k);
                                        return (
                                            <button
                                                key={k}
                                                type="button"
                                                onClick={() =>
                                                    set("eligible_kva", on
                                                        ? form.eligible_kva.filter((x) => x !== k)
                                                        : [...form.eligible_kva, k].sort((a, b) => a - b))
                                                }
                                                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                                                    on ? "border-primary bg-primary text-primary-foreground" : "border-input hover:bg-secondary"
                                                }`}
                                                data-testid={`f-kva-${k}`}
                                            >
                                                {k}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.green} onChange={(e) => set("green", e.target.checked)} data-testid="f-green" />
                            Offre verte (garantie d'origine)
                        </label>

                        <Field label="Notes internes">
                            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                                placeholder="Conditions de la négociation, contact, échéance…" data-testid="f-notes" />
                        </Field>

                        <DialogFooter>
                            <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-offer">
                                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement…</> : (editingId ? "Enregistrer les modifications" : "Enregistrer le prix négocié")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <p className="text-sm font-medium mb-1.5">{label}</p>
            {children}
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
}
